import React, { Component } from 'react';
import { connect } from 'react-redux';
import '../../src/index.css';

import ChatBoxMessageListItem from './ChatBoxMessageListItem';
import ChatBoxTypingIndicator from './ChatBoxTypingIndicator';
import Button from '@material-ui/core/Button';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';

// Anything within this many pixels of the bottom counts as "following along",
// so the list keeps auto-scrolling. Past it, new messages raise the pill
// instead of dragging the reader down.
const NEAR_BOTTOM_PX = 32;

// The app colours every message line by who wrote it (ChatBoxMessageListItem):
// terracotta for you, steel blue for them. The pill inherits that grammar.
const THEM = '#408ec9';
const YOU = '#d97c77';

export class ChatBoxMessageList extends Component {
    constructor(props) {
        super(props);
        this.textInput = React.createRef();
        this.state = {
            scrollToBottom: this.scrollToBottom,
            lastMessageCount: props.messages.length,
            // The newest message this list has already accounted for, held by
            // identity rather than by count. Paging older history in grows the
            // array from the top, so a count on its own can no longer tell new
            // mail from history the reader asked for.
            lastNewest: props.messages.length ? props.messages[props.messages.length - 1] : null,
            unseenCount: 0,
            // createAt of the first message in the current unread run; marks
            // where the divider goes.
            firstUnseenAt: null,
            pinnedToBottom: true,
            pillHover: false,
            pillFocus: false,
        };
    }

    // Runs before the render that shows the new message, so the unseen count
    // and the marker land in the same paint.
    //
    // Counts back from the end until it meets the message it last knew as the
    // newest: everything past that point arrived, everything before it was
    // already here. Pressing "Load more messages..." now really does lengthen
    // props.messages, so a plain length diff would announce the reader's own
    // history request as a pile of unread mail.
    static getDerivedStateFromProps(props, state) {
        const length = props.messages.length;
        const newest = length ? props.messages[length - 1] : null;
        if (newest === state.lastNewest && length === state.lastMessageCount) {
            return null;
        }
        const resynced = { lastMessageCount: length, lastNewest: newest };

        let appended = 0;
        if (state.lastNewest === null) {
            appended = length;
        } else {
            let i = length - 1;
            while (i >= 0 && props.messages[i] !== state.lastNewest) {
                appended++;
                i--;
            }
            if (i < 0) {
                // The message this list knew as newest is not in the array at
                // all, so the conversation was replaced rather than added to.
                // Resync quietly instead of calling all of it unread.
                appended = 0;
            }
        }

        if (appended === 0) {
            // Older history, or nothing that changes the bottom of the list.
            // Leave any marker standing - the reader has not caught up just
            // because they looked further back.
            return resynced;
        }
        if (state.pinnedToBottom) {
            // Watching live, so an earlier boundary marker has done its job.
            return { ...resynced, firstUnseenAt: null };
        }
        const firstNew = props.messages[length - appended];
        return {
            ...resynced,
            unseenCount: state.unseenCount + appended,
            // Only the start of a run gets the marker. Later arrivals join the
            // run rather than dragging the line down past messages still unread.
            firstUnseenAt: (state.unseenCount === 0 && firstNew)
                ? firstNew.createAt
                : state.firstUnseenAt,
        };
    };

    componentDidMount() {
        this.scrollToBottom();
        //this.props.onRef(this);
        //ReactDOM.findDOMNode(this.refs.click).addEventListener('scroll', this.listenScrollEvent);
    };

    componentWillUnmount() {
        //this.props.onRef(undefined)
        //ReactDOM.findDOMNode(this.refs.click).removeEventListener('scroll', this.listenScrollEvent);
    };

    //listenScrollEvent = () => {
    //  console.log('Scroll event detected!');
    //  this.scrollToBottom();
    //}

    // "Load more messages..." prepends older messages above whatever the reader
    // is looking at. Record how far the viewport sits from the bottom before
    // React re-renders, so componentDidUpdate can restore that distance rather
    // than scrolling to the end.
    //
    // The prepend is read off the props here rather than flagged by the click
    // handler: the page arrives from the server some renders later, and any
    // tick in between would have consumed the flag before the messages landed.
    getSnapshotBeforeUpdate(prevProps) {
        const grew = this.props.messages.length > prevProps.messages.length;
        const prepended = grew
            && prevProps.messages.length > 0
            && this.props.messages[0] !== prevProps.messages[0];
        if (prepended && this.scrollContainer) {
            return this.scrollContainer.scrollHeight - this.scrollContainer.scrollTop;
        }
        return null;
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (typeof snapshot === 'number' && this.scrollContainer) {
            // Same distance from the bottom as before the render, so the message
            // being read stays put while the older ones fill in above it.
            this.scrollContainer.scrollTop = this.scrollContainer.scrollHeight - snapshot;
            return;
        }
        // Reading back through history? Stay put. getDerivedStateFromProps has
        // already counted what arrived, and the pill offers the trip down.
        if (this.state.pinnedToBottom) {
            this.autoScrollToBottom();
        }
    };

    isNearBottom = () => {
        const el = this.scrollContainer;
        if (!el) {
            return true;
        }
        return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    };

    handleScroll = () => {
        const near = this.isNearBottom();
        if (near === this.state.pinnedToBottom && !(near && this.state.unseenCount > 0)) {
            return;
        }
        // Scrolling back down on your own counts as catching up, so the pill
        // retires quietly rather than waiting to be clicked.
        this.setState({ pinnedToBottom: near, unseenCount: near ? 0 : this.state.unseenCount });
    };

    jumpToNewest = () => {
        const el = this.scrollContainer;
        if (el) {
            const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            el.scrollTo({ top: el.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
        }
        this.setState({ unseenCount: 0, pinnedToBottom: true, pillHover: false, pillFocus: false });
    };

    unseenLabel = () => {
        const count = this.state.unseenCount;
        const newest = this.props.messages[this.props.messages.length - 1];
        const name = this.props.recipient && this.props.recipient.username;
        // One of your own can land here when a second window is open on the same chat.
        if (newest && this.props.profile && newest.userId === this.props.profile.id) {
            return 'Jump to newest';
        }
        if (!name) {
            return count === 1 ? 'New message below' : count + ' new messages below';
        }
        return count === 1 ? 'New message from ' + name : count + ' new messages from ' + name;
    };

    // Moves the view and nothing else, so following along live cannot rub out
    // the divider the reader may still want to scroll back up to.
    autoScrollToBottom = () => {
        if (this.messagesEnd) {
            this.messagesEnd.scrollIntoView({ behavior: "instant" });
        }
    };

    // The parent-facing version, called when a tab is switched to. Arriving at a
    // conversation starts it fresh, so the boundary marker is retired with it.
    scrollToBottom = () => {
        this.autoScrollToBottom();
        if (this.state.unseenCount !== 0 || !this.state.pinnedToBottom || this.state.firstUnseenAt !== null) {
            this.setState({ unseenCount: 0, pinnedToBottom: true, firstUnseenAt: null });
        }
    };

    scrollToBottomDelay = () => {
        setTimeout(function () {
            if (this.messagesEnd !== null) {
                this.messagesEnd.scrollIntoView({ behavior: "instant" });
                // ChatBoxWindow renders this list without the callback.
                if (this.props.callbackFromParentClick) {
                    this.props.callbackFromParentClick();
                }
            }
        }.bind(this), 50);
    };

    // No slice any more: what this holds is exactly what was fetched, and
    // fetching more is a deliberate press of the button rather than something
    // the renderer decides.
    //
    // No sort either. The parent already hands these over oldest first: each
    // page comes back from api/Channel/GetMessages in the server's
    // (CreateAt, Id) order, older pages go on the front and live messages on
    // the end. That Id tiebreaker is a SQL Server uniqueidentifier order,
    // which no comparison of the id strings here would reproduce, so a client
    // sort could only ever disagree with it. And the two sorts that used to
    // run here were worse than that: both sorted the parent's array in place
    // during render, and one answered -1 for a tie, so messages sharing a
    // createAt swapped places on every render.
    //
    // The day boundaries go on copies. Writing firstMessageOfDay onto the
    // parent's objects meant it was never taken off again, so after "Load
    // more messages..." put an earlier message on the same day above it, the
    // old first-of-day kept its flag and the date header showed twice.
    processMessages = (messages) => {
        var currentDay = "";
        return messages.map((message) => {
            var messageDay = new Date(message.createAt).toLocaleString().split(',')[0];
            var firstMessageOfDay = messageDay !== currentDay;
            currentDay = messageDay;
            return { ...message, firstMessageOfDay: firstMessageOfDay };
        });
    };

    loadOlder = () => {
        if (this.props.onLoadOlder) {
            this.props.onLoadOlder();
        }
    };

    render() {
        let messageButton;
        const messages = [];

        //console.log("number of messages", this.props.messages.length);

        // hasMore is the server's answer, so the button disappears at the true
        // top of the conversation rather than after a press that comes back
        // empty. ChatBoxWindow renders this list without any of these props;
        // it simply gets no button, as before.
        if (this.props.hasMore) {
            messageButton = <Button variant="contained" /*color="primary"*/ style={{ boxShadow: "none", marginTop: '10px'}} disableFocusRipple={true} disableRipple={true} fullWidth={true} type="submit" disabled={!!this.props.loadingOlder} onClick={this.loadOlder}>{this.props.loadingOlder ? 'Loading...' : 'Load more messages...'}</Button>;
        }

        var processedMessages = this.processMessages(this.props.messages);

        // Match the first occurrence by index so two messages sharing a
        // millisecond could never draw the divider twice.
        const unseenIndex = this.state.firstUnseenAt === null
            ? -1
            : processedMessages.findIndex((m) => m.createAt === this.state.firstUnseenAt);

        // Keyed by post id where there is one. An index key shifts every row
        // when an older page lands on top, so each item was handed a
        // different message and its hover state stayed on the wrong line.
        processedMessages.forEach((message, i) => {
            messages.push(
                <ChatBoxMessageListItem message={message} key={message.id ? message.id : 'index-' + i} firstUnseen={i === unseenIndex} />
            );
        });

        let messageListHeight;
        if (this.props.recipient.awayMessage !== null && this.props.recipient.awayMessage !== "" && this.props.recipient.status === "Done" && this.props.recipient.inOffice === false) {
            messageListHeight = { paddingLeft: '10px', paddingRight: '10px', overflow: 'auto', position: 'absolute', top: '66px', bottom: '92px', left: '0', right: '0' };
        } else if ((this.props.recipient.awayMessage !== null && this.props.recipient.awayMessage !== "") && (this.props.recipient.awayTime !== null && this.props.recipient.awayTime !== "") && this.props.recipient.status !== "Done") {
            messageListHeight = { paddingLeft: '10px', paddingRight: '10px', overflow: 'auto', position: 'absolute', top: '47px', bottom: '92px', left: '0', right: '0' };
        } else if (this.props.recipient.status === "Done" && this.props.recipient.inOffice === false && (this.props.recipient.awayMessage === "" || this.props.recipient.awayMessage === null)) {
            messageListHeight = { paddingLeft: '10px', paddingRight: '10px', overflow: 'auto', position: 'absolute', top: '47px', bottom: '92px', left: '0', right: '0' };
        } else {
            messageListHeight = { paddingLeft: '10px', paddingRight: '10px', overflow: 'auto', position: 'absolute', top: '28px', bottom: '92px', left: '0', right: '0' };
        }

        const pillShown = this.state.unseenCount > 0 && !this.state.pinnedToBottom;
        const pillLit = this.state.pillHover || this.state.pillFocus;

        return (
            <div style={messageListHeight} ref={(el) => { this.scrollContainer = el; }} onScroll={this.handleScroll}>
                {messageButton}
                <div>
                    {messages}
                </div>
                {/* The live region is always here so that the indicator arriving
                    inside it is a single polite announcement, rather than the
                    region itself coming and going. It sits in the message flow
                    above the messagesEnd anchor, so componentDidUpdate's
                    auto-scroll keeps it in view while pinned to the bottom with
                    no change to the scroll logic - and reading back through
                    history leaves it off-screen, where the pill is what speaks.
                    Deliberately not in the sticky rail below: it would collide
                    with the pill at narrow widths. */}
                <div aria-live="polite">
                    {this.props.typingName
                        ? <ChatBoxTypingIndicator name={this.props.typingName} />
                        : null}
                </div>
                <div
                    containerid="options-holder"
                    style={{ float: "left", clear: "both" }}
                    ref={(el) => { this.messagesEnd = el; }}
                >
                </div>
                {/* A zero-height sticky rail pinned to the bottom of the scrollport, so
                    the pill hovers over the conversation without occupying a line of it.
                    Styled inline on purpose: MyWindowPortal's copyStyles is commented
                    out, so a detached chat window inherits no stylesheet at all. */}
                <div style={{ position: 'sticky', bottom: '0px', height: '0px', clear: 'both', zIndex: 2 }}>
                    <div
                        aria-live="polite"
                        style={{ position: 'absolute', bottom: '10px', left: '0px', right: '0px', display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}
                    >
                        <button
                            type="button"
                            onClick={this.jumpToNewest}
                            onMouseEnter={() => this.setState({ pillHover: true })}
                            onMouseLeave={() => this.setState({ pillHover: false })}
                            onFocus={() => this.setState({ pillFocus: true })}
                            onBlur={() => this.setState({ pillFocus: false })}
                            tabIndex={pillShown ? 0 : -1}
                            aria-hidden={!pillShown}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                maxWidth: '92%',
                                padding: '5px 10px 5px 12px',
                                border: '1px solid ' + THEM,
                                borderRadius: '14px',
                                backgroundColor: pillLit ? THEM : '#ffffff',
                                color: pillLit ? '#ffffff' : THEM,
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                                fontSize: '12px',
                                fontWeight: '500',
                                lineHeight: '16px',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                // Terracotta is "you" everywhere else in this chat, which
                                // makes it the one ring that cannot be mistaken for the
                                // blue it sits on.
                                outline: this.state.pillFocus ? '2px solid ' + YOU : 'none',
                                outlineOffset: '2px',
                                boxShadow: pillLit ? '0 2px 8px rgba(64,142,201,0.35)' : '0 1px 6px rgba(0,0,0,0.16)',
                                opacity: pillShown ? 1 : 0,
                                transform: pillShown ? 'translateY(0)' : 'translateY(6px)',
                                pointerEvents: pillShown ? 'auto' : 'none',
                                transition: 'opacity 140ms ease-out, transform 140ms ease-out, background-color 120ms ease-out, color 120ms ease-out, box-shadow 120ms ease-out',
                            }}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{this.unseenLabel()}</span>
                            <KeyboardArrowDown style={{ fontSize: '16px', marginLeft: '4px', flexShrink: 0 }} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        members: state.members,
        profile: state.profile,
        currentUser: state.currentUser,
    };
}

export default connect(mapStateToProps, null, null, { withRef: true })(ChatBoxMessageList);


// WEBPACK FOOTER //
// ./src/components/ChatBoxMessageList.js